import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import proxy from '../api/axios';
import { formatScientificText } from '../common/formatScientificText';

const ACCENT = '#137274';

const STATUS_META = {
  correct: { label: 'Correct', color: '#1a7f37', icon: CheckCircleOutlineIcon },
  incorrect: { label: 'Incorrect', color: '#cf222e', icon: CancelOutlinedIcon },
  unattempted: { label: 'Unattempted', color: '#8b949e', icon: RemoveCircleOutlineIcon },
};

const NeetTestResult = () => {
  const { testId } = useParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark ? '#0f1419' : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 45%, #ffffff 100%)';

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSubject, setActiveSubject] = useState('all');

  const reviewItems = useMemo(
    () => (result?.review || []).map((item, index) => ({ ...item, _displayIndex: index })),
    [result],
  );

  const subjects = useMemo(() => {
    const seen = [];
    reviewItems.forEach((item) => {
      const s = item.subject || 'General';
      if (!seen.includes(s)) seen.push(s);
    });
    return seen;
  }, [reviewItems]);

  const subjectStats = useMemo(() => {
    const stats = {};
    reviewItems.forEach((item) => {
      const s = item.subject || 'General';
      if (!stats[s]) stats[s] = { total: 0, correct: 0, incorrect: 0, unattempted: 0, marks: 0 };
      stats[s].total += 1;
      stats[s][item.status] = (stats[s][item.status] || 0) + 1;
      stats[s].marks += item.marksAwarded;
    });
    return stats;
  }, [reviewItems]);

  const visibleItems = useMemo(
    () => (activeSubject === 'all' ? reviewItems : reviewItems.filter((item) => (item.subject || 'General') === activeSubject)),
    [reviewItems, activeSubject],
  );

  useEffect(() => {
    let cancelled = false;
    const fetchResult = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await proxy.get(`/neet-tests/${testId}/result`);
        if (!cancelled) setResult(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Could not load result.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchResult();
    return () => {
      cancelled = true;
    };
  }, [testId]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, bgcolor: pageBg }}>
        <CircularProgress size={28} sx={{ color: ACCENT }} />
        <Typography sx={{ color: muted, fontWeight: 600 }}>Loading result…</Typography>
      </Box>
    );
  }

  if (error || !result) {
    return (
      <Box sx={{ minHeight: '60vh', bgcolor: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 420 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 0.75 }}>
            {error || 'Result not available yet.'}
          </Typography>
          <Button component={Link} to="/neet-tests" variant="contained" sx={{ mt: 1, textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } }}>
            Back to tests
          </Button>
        </Box>
      </Box>
    );
  }

  const scorePercent = result.maxScore > 0 ? Math.round((result.score / result.maxScore) * 100) : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: pageBg, px: { xs: 1.5, sm: 3 }, py: 3 }}>
      <Helmet>
        <title>{result.testTitle} · Result | CrafterHack</title>
      </Helmet>
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
          <Button component={Link} to="/neet-tests" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none', fontWeight: 700, color: muted, px: 0, '&:hover': { bgcolor: 'transparent', color: ACCENT } }}>
            Back to tests
          </Button>
          <Button
            component={Link}
            to={`/neet-tests/${testId}/leaderboard`}
            startIcon={<LeaderboardOutlinedIcon />}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 700, borderColor: border }}
          >
            View leaderboard
          </Button>
        </Box>

        <Box sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2.5, border: `1px solid ${border}`, bgcolor: surface, mb: 3 }}>
          <Typography variant="body2" sx={{ color: muted, fontWeight: 700, mb: 0.5 }}>
            {result.testTitle}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: '2rem', sm: '2.4rem' }, letterSpacing: '-0.04em', color: ACCENT }}>
              {result.score}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.05rem', sm: '1.2rem' }, color: muted }}>/ {result.maxScore}</Typography>
            <Chip size="small" label={`${scorePercent}%`} sx={{ fontWeight: 800, bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1), color: ACCENT }} />
            {result.autoSubmitted && (
              <Chip size="small" label="Auto-submitted (time up)" sx={{ fontWeight: 700, bgcolor: alpha('#d97706', isDark ? 0.22 : 0.1), color: '#d97706' }} />
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<CheckCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
              label={`${result.correctCount} correct`}
              sx={{ fontWeight: 700, bgcolor: alpha('#1a7f37', isDark ? 0.22 : 0.1), color: '#1a7f37' }}
            />
            <Chip
              icon={<CancelOutlinedIcon sx={{ fontSize: '16px !important' }} />}
              label={`${result.incorrectCount} incorrect`}
              sx={{ fontWeight: 700, bgcolor: alpha('#cf222e', isDark ? 0.22 : 0.1), color: '#cf222e' }}
            />
            <Chip
              icon={<RemoveCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
              label={`${result.unattemptedCount} unattempted`}
              sx={{ fontWeight: 700, bgcolor: alpha('#8b949e', isDark ? 0.22 : 0.1), color: '#8b949e' }}
            />
          </Box>

          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: muted }}>
            Marking scheme: +{result.marksCorrect} for correct
            {result.allowNegativeMarking ? `, −${result.marksIncorrect} for incorrect (unless a question opts out)` : ', no negative marking on this test'}.
          </Typography>
        </Box>

        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1.5 }}>Question review</Typography>

        {subjects.length > 1 && (
          <Box sx={{ borderBottom: `1px solid ${border}`, mb: 2 }}>
            <Tabs
              value={activeSubject}
              onChange={(_, v) => setActiveSubject(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem' } }}
            >
              <Tab value="all" label={`All (${reviewItems.length})`} />
              {subjects.map((s) => (
                <Tab key={s} value={s} label={`${s} (${subjectStats[s].total})`} />
              ))}
            </Tabs>
          </Box>
        )}

        {activeSubject !== 'all' && subjectStats[activeSubject] && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            <Chip size="small" label={`${subjectStats[activeSubject].correct || 0} correct`} sx={{ fontWeight: 700, bgcolor: alpha('#1a7f37', isDark ? 0.22 : 0.1), color: '#1a7f37' }} />
            <Chip size="small" label={`${subjectStats[activeSubject].incorrect || 0} incorrect`} sx={{ fontWeight: 700, bgcolor: alpha('#cf222e', isDark ? 0.22 : 0.1), color: '#cf222e' }} />
            <Chip size="small" label={`${subjectStats[activeSubject].unattempted || 0} unattempted`} sx={{ fontWeight: 700, bgcolor: alpha('#8b949e', isDark ? 0.22 : 0.1), color: '#8b949e' }} />
            <Chip
              size="small"
              label={`${subjectStats[activeSubject].marks > 0 ? '+' : ''}${subjectStats[activeSubject].marks} marks`}
              sx={{ fontWeight: 800, bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1), color: ACCENT }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {visibleItems.map((item) => {
            const meta = STATUS_META[item.status] || STATUS_META.unattempted;
            const StatusIcon = meta.icon;
            return (
              <Box key={item.questionId} sx={{ p: 2, borderRadius: 2, border: `1px solid ${alpha(meta.color, 0.3)}`, bgcolor: surface }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={`Q${item._displayIndex + 1}`} sx={{ fontWeight: 800 }} />
                  {item.subject && <Chip size="small" variant="outlined" label={item.subject} sx={{ fontWeight: 700 }} />}
                  <Chip
                    size="small"
                    icon={<StatusIcon sx={{ fontSize: '15px !important' }} />}
                    label={meta.label}
                    sx={{ fontWeight: 800, bgcolor: alpha(meta.color, isDark ? 0.22 : 0.1), color: meta.color }}
                  />
                  <Typography sx={{ ml: 'auto', fontWeight: 800, fontSize: '0.85rem', color: item.marksAwarded > 0 ? '#1a7f37' : item.marksAwarded < 0 ? '#cf222e' : muted }}>
                    {item.marksAwarded > 0 ? `+${item.marksAwarded}` : item.marksAwarded}
                  </Typography>
                </Box>

                <Typography
                  sx={{ fontWeight: 700, fontSize: '0.95rem', mb: item.image ? 1 : 1.5 }}
                  dangerouslySetInnerHTML={{ __html: formatScientificText(item.questionText) }}
                />

                {item.image && (
                  <Box
                    component="img"
                    src={item.image}
                    alt=""
                    sx={{ display: 'block', maxWidth: '100%', maxHeight: 280, borderRadius: 1.5, border: `1px solid ${border}`, mb: 1.5 }}
                  />
                )}

                {item.type === 'mcq' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                    {(item.options || []).map((option, i) => {
                      const isCorrectOption = i === item.correctOptionIndex;
                      const isUserOption = i === item.userSelectedOptionIndex;
                      const optionImage = item.optionImages?.[i];
                      return (
                        <Box
                          key={i}
                          sx={{
                            px: 1.25,
                            py: 0.6,
                            borderRadius: 1.25,
                            fontSize: '0.85rem',
                            border: `1px solid ${isCorrectOption ? alpha('#1a7f37', 0.5) : isUserOption ? alpha('#cf222e', 0.5) : border}`,
                            bgcolor: isCorrectOption ? alpha('#1a7f37', isDark ? 0.18 : 0.08) : isUserOption ? alpha('#cf222e', isDark ? 0.18 : 0.08) : 'transparent',
                            fontWeight: isCorrectOption || isUserOption ? 700 : 500,
                          }}
                        >
                          {optionImage && (
                            <Box component="img" src={optionImage} alt="" sx={{ display: 'block', maxWidth: '100%', maxHeight: 140, borderRadius: 1, mb: option ? 0.5 : 0 }} />
                          )}
                          {option && <Box component="span" dangerouslySetInnerHTML={{ __html: formatScientificText(option) }} />}
                          {isCorrectOption ? '  ✓ Correct answer' : isUserOption ? '  ✗ Your answer' : ''}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, fontSize: '0.85rem' }}>
                    <Typography variant="body2">
                      <b>Your answer:</b>{' '}
                      {item.userAnswerText ? (
                        <Box component="span" dangerouslySetInnerHTML={{ __html: formatScientificText(item.userAnswerText) }} />
                      ) : (
                        <em style={{ color: muted }}>Not answered</em>
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a7f37' }}>
                      <b>Accepted answer{item.correctAnswers?.length > 1 ? 's' : ''}:</b>{' '}
                      <Box
                        component="span"
                        dangerouslySetInnerHTML={{ __html: formatScientificText((item.correctAnswers || []).join(', ')) }}
                      />
                    </Typography>
                  </Box>
                )}

                {item.explanation && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1.25, color: muted, fontStyle: 'italic' }}
                    dangerouslySetInnerHTML={{ __html: formatScientificText(item.explanation) }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default NeetTestResult;
